import type { TablePaginationConfig } from 'antd/es/table';
import {
    isCanceledRequest,
    useCancelableRequest,
} from 'mixins/hooks/useCancelableRequest';
import { useCallback, useEffect, useState } from 'react';
import {
    blockPublicApiToken,
    deletePublicApiTokenByTaxpayerCode,
    getPublicApiTokenList,
    unblockPublicApiToken,
} from '../actions';
import { GenerationsData } from '../admin-token-generations';
import {
    DEFAULT_PAGE_SIZE,
    DEFAULT_SORT,
} from '../utils/constants';

export const useTokenGenerations = () => {
    const {
        createRequestSignal: createTokenListRequestSignal,
        finishRequest: finishTokenListRequest,
    } = useCancelableRequest();
    const [data, setData] = useState<GenerationsData[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Значение, которое пользователь сейчас вводит.
    const [taxpayerCode, setTaxpayerCode] = useState('');

    // Значение, которое реально передаётся на backend.
    const [appliedTaxpayerCode, setAppliedTaxpayerCode] =
        useState<string | undefined>();

    const [pagination, setPagination] =
        useState<TablePaginationConfig>({
            current: 1,
            pageSize: DEFAULT_PAGE_SIZE,
        });

    const fetchData = useCallback(() => {
        const signal = createTokenListRequestSignal();
        setIsLoading(true);

        const params = {
            page: (pagination.current || 1) - 1,
            size: pagination.pageSize || DEFAULT_PAGE_SIZE,
            sort: DEFAULT_SORT,
            ...(appliedTaxpayerCode
                ? {
                      taxpayerCode: appliedTaxpayerCode,
                  }
                : {}),
        };

        return getPublicApiTokenList(params, signal)
            .then((response: any) => {
                setData(response.data.content || []);
                setPagination((current) => ({
                    ...current,
                    total: response.data.totalElements || 0,
                }));
            })
            .catch((error) => {
                if (!isCanceledRequest(error)) {
                    throw error;
                }
            })
            .finally(() => {
                if (finishTokenListRequest(signal)) {
                    setIsLoading(false);
                }
            });
    }, [
        appliedTaxpayerCode,
        createTokenListRequestSignal,
        finishTokenListRequest,
        pagination.current,
        pagination.pageSize,]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getTokenAction = (
        id: string,
        action: 'block' | 'unblock' | 'delete',
        taxpayerCode?: string
    ) => {
        switch (action) {
            case 'block':
                return blockPublicApiToken(id);
            case 'unblock':
                return unblockPublicApiToken(id);
            case 'delete':
                if (!taxpayerCode) {
                    return Promise.reject(
                        new Error('Taxpayer code is required for token delete action')
                    );
                }

                return deletePublicApiTokenByTaxpayerCode(taxpayerCode);
            default:
                return Promise.reject(
                    new Error(`Unknown token action: ${action}`)
                );
        }
    };

    const handleTokenAction = useCallback(
        (id: string, action: 'block' | 'unblock' | 'delete', taxpayerCode?: string) => {
            setIsLoading(true);

            getTokenAction(id, action, taxpayerCode)
                .then(fetchData)
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [fetchData]
    );

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleGenerateSuccess = () => {
        setIsModalOpen(false);
        fetchData();
    };

    const handlePageChange = (page: number) => {
        setPagination((current) => ({
            ...current,
            current: page,
        }));
    };

    const handlePageSizeChange = (
        _current: number,
        size: number
    ) => {
        setPagination((current) => ({
            ...current,
            current: 1,
            pageSize: size,
        }));
    };

    const handleTaxpayerCodeChange = (value: string) => {
        const normalizedValue = value
            .replace(/\D/g, '')
            .slice(0, 12);

        setTaxpayerCode(normalizedValue);
    };

    const handleTaxpayerCodeSearch = () => {
        const normalizedValue = taxpayerCode.trim();

        if (normalizedValue.length !== 12) {
            return;
        }

        setPagination((current) => ({
            ...current,
            current: 1,
        }));

        setAppliedTaxpayerCode(normalizedValue);
    };

    const handleTaxpayerCodeReset = () => {
        setTaxpayerCode('');
        setAppliedTaxpayerCode(undefined);

        setPagination((current) => ({
            ...current,
            current: 1,
        }));
    };

    return {
        data,
        handleCancel,
        handleGenerateSuccess,
        handlePageChange,
        handlePageSizeChange,
        handleTaxpayerCodeChange,
        handleTaxpayerCodeReset,
        handleTaxpayerCodeSearch,
        handleTokenAction,
        isLoading,
        isModalOpen,
        openModal: () => setIsModalOpen(true),
        pagination,
        taxpayerCode,
    };
};